import { Checkbox } from '@ipfabric/ui-components/Checkbox/Checkbox';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import { LabeledControl } from '@ipfabric/ui-components/LabeledControl/LabeledControl';
import type { InteractiveAtomSize } from '@ipfabric/ui-components/utils/InteractiveAtomProps';
import type { StringKey } from '@ipfabric/utils';
import type { FormikProps } from 'formik';
import type { JSX, ReactNode } from 'react';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

interface Props<TFormikValues> {
  className?: string;
  disabled?: boolean;
  formik: FormikProps<TFormikValues>;
  name: StringKey<TFormikValues> | NestedFormikName;
  size?: InteractiveAtomSize | undefined;
  error?: string;
  description?: ReactNode;
  helperText?: ReactNode;
}

/*
 FormikCheckbox component combines Checkbox from new design system with legacy Formik wrapper, which will be replaced by HookForm implementation in the future.
 Note: Use this component only inside Formik forms where checkbox from new design system is needed.
*/
export const FormikCheckbox = <TFormikValues,>({
  className,
  disabled,
  formik,
  name,
  size,
  error,
  description,
  helperText,
  ...restProps
}: Props<TFormikValues>): JSX.Element => {
  const field = formik.getFieldProps(name);
  const handleChange = (value: boolean) => {
    field.onChange({
      target: {
        name: field.name,
        value,
      },
    });
  };
  return (
    <FormField disabled={disabled} error={error} size={size}>
      <LabeledControl
        control={
          <Checkbox
            {...field}
            {...restProps}
            onChange={handleChange}
            size={size}
            hasError={error !== undefined}
            disabled={disabled}
          />
        }
        description={description}
        helperText={helperText}
        size={size}
        disabled={disabled}
        className='font-semibold'
      />
    </FormField>
  );
};
